"use client";

import type { ReactNode } from "react";
import { useEffect, useRef, useState } from "react";
import { usePathname } from "next/navigation.js";
import { cn } from "@rosette/ts/src/util/cn.js";
import { BodyPortal } from "../generic/BodyPortal.js";


interface ModalProps {
    open: boolean;
    onClose: () => void;
    /** Applies to the box that holds the children, such as its width. */
    className?: string;
    children?: ReactNode;
}


/**
 * Content shown above a dimmed page, which is blocked until it closes.
 * Escape, clicks on the dimmed page and navigating to another page call
 * onClose. Put a Card inside.
 */
export function Modal({ open, onClose, className, children }: ModalProps) {
    // State rather than a ref, because the portal mounts the dialog
    // after the first render, and opening it must wait for it.
    const [dialog, setDialog] = useState<HTMLDialogElement | null>(null);

    // A native <dialog> gives the top layer, focus trapping and Escape,
    // but only when opened through showModal().
    useEffect(() => {
        if (!dialog)
            return;
        if (open && !dialog.open) {
            dialog.showModal();
            // showModal() focuses the first control, which draws its focus
            // ring before the user has done anything. Focus the dialog instead,
            // so the first Tab reaches that control.
            dialog.focus();
        }
        if (!open && dialog.open)
            dialog.close();
    }, [open, dialog]);

    const pathname = usePathname();
    const pathnameRef = useRef(pathname);
    useEffect(() => {
        if (pathname === pathnameRef.current)
            return;
        pathnameRef.current = pathname;
        if (open)
            onClose();
    }, [pathname, open, onClose]);

    // The portal keeps the modal from inheriting the text styles of where
    // it is rendered, such as the header's text-xl.
    return (
        <BodyPortal>
            <dialog
                ref={setDialog}
                tabIndex={-1}
                className={cn(
                    // The dialog is the dimmed page: it covers the viewport, so a
                    // click outside the box lands on it, and it fades in as one.
                    "size-full max-h-none max-w-none items-center justify-center p-3 open:flex",
                    "bg-scrim text-body outline-none backdrop:bg-transparent",
                    "transition-opacity duration-150 starting:open:opacity-0",
                    // Stops the page scrolling behind the open modal.
                    "[:root:has(&:modal)]:overflow-hidden",
                )}
                onCancel={(event) => {
                    event.preventDefault();
                    onClose();
                }}
                onClick={(event) => {
                    if (event.target === event.currentTarget)
                        onClose();
                }}>

                <div className={cn("max-h-full w-full max-w-lg overflow-y-auto", className ?? null)}>
                    {open && children}
                </div>
            </dialog>
        </BodyPortal>
    );
}
